/**
 * Production adapter for the Contour+ model-selection tools — binds the
 * React-free ports to the app: the loaded image (Recoil), the Contour+ model
 * list (the React Query cache the manual form's prefetch fills — no new
 * request), the `/classify-slices` route (CT and axial MR), and the Contour+
 * form request atom.
 * Like the other ops' engineDeps, this browser glue is covered by the manual
 * demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback, useContext, useEffect, useRef } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { useRecoilCallback, useSetRecoilState } from 'recoil';
import { queryKeyFn } from '~/backend/connector-api/connector-context';
import { APIType, type UserModels } from '~/backend/types';
import type { SliceClassificationModality } from '@agent/tool-names';
import type { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import type { Image3D } from '~/domain/patient/image3d';
import type { WindowLevel } from '~/domain/patient/windowLevel';
import { dcmjsGetString } from '~/domain/services/dicomProcessor';
import { agentContourModelRequestState } from '~/state/atoms/chatAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import {
    transformAvailableModelsData,
    type PredictionModel,
} from '~/utils/transforms/predictionModelsDataTransforms';
import type { SliceRegionLabel } from '../../contract';
import type { AnalyzeImageDeps, ImageForAnalysis } from './analyzeImage';
import {
    hasEnoughBody,
    isAxialStack,
    mrSignalThreshold,
    orderAlongSliceNormal,
} from './sampleSlices';
import { exposeModelCatalogue, logModelSelectionTrace } from './debugTrace';
import {
    chooseContourModels,
    loadFallbackCatalogue,
} from './fallbackCatalogue';
import type { ModelSelectionDeps } from './toolRunner';

/** Longest edge of the PNG sent for classification (px). */
const MAX_PNG_EDGE_PX = 256;

/**
 * Rescaled pixel values for the vision path. MR uses the Float32 copy — the
 * same values the volume's histogram window is computed from, with no Int16
 * range limit; CT keeps the Int16 HU array it has always used.
 */
function visionPixels(slice: DicomImageSlice): ArrayLike<number> {
    return slice.modality === 'MR' ? slice.floatPixelData : slice.pixelData;
}

/** Grayscale, downscaled, header-free PNG of one slice (base64, no prefix). */
async function renderSliceToPng(
    slice: DicomImageSlice,
    windowLevel: WindowLevel
): Promise<string> {
    const step = Math.max(
        1,
        Math.ceil(Math.max(slice.rows, slice.cols) / MAX_PNG_EDGE_PX)
    );
    const width = Math.floor(slice.cols / step);
    const height = Math.floor(slice.rows / step);
    const low = windowLevel.level - windowLevel.window / 2;
    const pixels = visionPixels(slice);
    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const value = pixels[y * step * slice.cols + x * step];
            const v = ((value - low) / windowLevel.window) * 255;
            const o = (y * width + x) * 4;
            rgba[o] = rgba[o + 1] = rgba[o + 2] = v;
            rgba[o + 3] = 255;
        }
    }
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('2D canvas unavailable');
    }
    ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
    const bytes = new Uint8Array(
        await (await canvas.convertToBlob({ type: 'image/png' })).arrayBuffer()
    );
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
}

/**
 * Window used to render (and, for MR, to judge "not air" on) the classified
 * slices. CT keeps the viewer's default window. MR intensities are
 * scanner-relative, so it uses the volume's histogram window (2nd–99.5th
 * percentile), then the DICOM window — never a user-adjusted one — so the
 * same series always yields the same PNGs.
 */
function visionWindowLevel(image: Image3D): WindowLevel {
    if (image.seriesMetadata.modality === 'MR') {
        return (
            image.getAutoWindowLevel() ??
            image.getAcquisitionWindowLevel() ??
            image.getDefaultWindowLevel()
        );
    }
    return image.getDefaultWindowLevel();
}

/** POST the PNGs to the stateless `/classify-slices` route. */
async function classifySlicesViaRoute(
    pngBase64: string[],
    modality: SliceClassificationModality,
    requestHeaders: Record<string, string>
): Promise<ReadonlyArray<SliceRegionLabel>> {
    const response = await fetch(AppConfig.agentClassifySlicesUrl, {
        method: 'POST',
        headers: { ...requestHeaders, 'content-type': 'application/json' },
        body: JSON.stringify({ images: pngBase64, modality }),
    });
    if (response.status === 404) {
        throw new Error(
            'image classification is not enabled on the agent server ' +
                '(AGENT_SLICE_CLASSIFICATION)'
        );
    }
    if (!response.ok) {
        throw new Error(
            `image classification failed (HTTP ${response.status})`
        );
    }
    const data = (await response.json()) as { regions: SliceRegionLabel[] };
    return data.regions;
}

const CONNECTOR_CONTOUR_MODELS_KEY = queryKeyFn({
    path: '/proxy/{rest}',
    operationId: 'getProxied',
    variables: { pathParams: { rest: 'contouring/list-active-models' } },
});

export type ModelSelectionEngineDeps = AnalyzeImageDeps<DicomImageSlice> &
    Omit<ModelSelectionDeps, 'pendingMutationExists'>;

/**
 * @param agentRequestHeaders - headers for agent server requests (sign-in
 *   token, test user), resolved per request.
 */
export function useModelSelectionDeps(
    agentRequestHeaders: () => Promise<Record<string, string>>
): () => ModelSelectionEngineDeps {
    // Read the context directly (not useQueryClient) so the chat still mounts
    // without a QueryClientProvider; the model list is then "not loaded".
    const queryClient = useContext(QueryClientContext);
    const setRequest = useSetRecoilState(agentContourModelRequestState);
    const setWorkflowModal = useSetRecoilState(workflowModalState);

    // Optional saved catalogue (agentModelCatalogueUrl), used only while the
    // live model list is not loaded. Fetched once; empty URL = no fallback.
    const fallbackModels = useRef<ReadonlyArray<PredictionModel>>();
    useEffect(() => {
        let cancelled = false;
        void loadFallbackCatalogue(AppConfig.agentModelCatalogueUrl).then(
            (models) => {
                if (!cancelled) {
                    fallbackModels.current = models;
                }
            }
        );
        return () => {
            cancelled = true;
        };
    }, []);

    const readImage = useRecoilCallback(
        ({ snapshot }) =>
            async (): Promise<
                ImageForAnalysis<DicomImageSlice> | undefined
            > => {
                const image = await snapshot.getPromise(primaryImageState);
                if (!image) {
                    return undefined;
                }
                const slices = image.series.dicomImageSlices;
                const first = slices[0];
                const modality = image.seriesMetadata.modality;
                // CT: fixed HU air threshold. MR: relative to the render window.
                const bodyThreshold =
                    modality === 'MR'
                        ? (() => {
                              const wl = visionWindowLevel(image);
                              return mrSignalThreshold(wl.level, wl.window);
                          })()
                        : undefined;
                return {
                    modality,
                    hints: {
                        bodyPartExamined: first
                            ? dcmjsGetString(
                                  first.datasetWrapper,
                                  'BodyPartExamined'
                              )
                            : '',
                        seriesDescription: first?.seriesDescription ?? '',
                        protocolName: first
                            ? dcmjsGetString(
                                  first.datasetWrapper,
                                  'ProtocolName'
                              )
                            : '',
                    },
                    orderedSlices: orderAlongSliceNormal(slices),
                    isAxial: first
                        ? isAxialStack(first.imageOrientation)
                        : false,
                    isUsable: (slice) =>
                        dcmjsGetString(
                            slice.datasetWrapper,
                            'BurnedInAnnotation'
                        ) !== 'YES' &&
                        hasEnoughBody(visionPixels(slice), bodyThreshold),
                };
            },
        []
    );

    // Render window for the classified slices (see visionWindowLevel).
    const readWindowLevel = useRecoilCallback(
        ({ snapshot }) =>
            async (): Promise<WindowLevel> => {
                const image = await snapshot.getPromise(primaryImageState);
                if (!image) {
                    throw new Error('no image loaded');
                }
                return visionWindowLevel(image);
            },
        []
    );

    return useCallback((): ModelSelectionEngineDeps => {
        const contourModels = ():
            | ReadonlyArray<PredictionModel>
            | undefined => {
            if (!queryClient) {
                return fallbackModels.current;
            }
            const raw = AppConfig.isConnectorMode
                ? queryClient.getQueryData<UserModels>(
                      CONNECTOR_CONTOUR_MODELS_KEY
                  )
                : undefined;
            const models = chooseContourModels(
                AppConfig.isConnectorMode
                    ? raw && transformAvailableModelsData(raw)
                    : queryClient.getQueryData<PredictionModel[]>([
                          'models',
                          APIType.CONTOURING,
                      ]),
                fallbackModels.current
            );
            if (import.meta.env.DEV && models) {
                exposeModelCatalogue(models);
            }
            return models;
        };
        return {
            image: readImage,
            contourModels,
            renderPng: async (slice) =>
                renderSliceToPng(slice, await readWindowLevel()),
            classify: async (pngBase64, modality) =>
                classifySlicesViaRoute(
                    pngBase64,
                    modality,
                    await agentRequestHeaders()
                ),
            // Dev server only: prints the decision trace to DevTools.
            onTrace: import.meta.env.DEV ? logModelSelectionTrace : undefined,
            requestForm: (request) => setRequest(request),
            openContourForm: () =>
                setWorkflowModal({ open: true, page: WorkflowTypes.CONTOUR }),
        };
    }, [
        agentRequestHeaders,
        queryClient,
        readImage,
        readWindowLevel,
        setRequest,
        setWorkflowModal,
    ]);
}
